import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CalendarIcon,
  ChevronRightIcon,
  MessageSquareIcon,
} from "lucide-react";
import InquiryStatusBadge from "@/polymet/components/inquiry-status-badge";

export interface InquiryListItemProps {
  id: string;
  subject: string;
  status: "new" | "pending" | "responded" | "closed" | "expired";
  createdAt: string;
  updatedAt: string;
  productImage: string;
  productName: string;
  buyerName: string;
  sellerName: string;
  currentUserId: string;
  buyerId: string;
  sellerId: string;
  hasUnreadMessages: boolean;
  lastMessagePreview?: string;
}

export default function InquiryListItem({
  id,
  subject,
  status,
  createdAt,
  updatedAt,
  productImage,
  productName,
  buyerName,
  sellerName,
  currentUserId,
  buyerId,
  sellerId,
  hasUnreadMessages,
  lastMessagePreview,
}: InquiryListItemProps) {
  const isBuyer = currentUserId === buyerId;
  const isSeller = currentUserId === sellerId;

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffDays < 7) {
      return `${date.toLocaleDateString([], { weekday: "short" })} at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      return date.toLocaleDateString([], { day: "numeric", month: "short" });
    }
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden ${hasUnreadMessages ? "border-primary" : ""}`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Product Image */}
        <div className="w-full md:w-1/4 lg:w-1/5 p-4 bg-muted/30">
          <div className="aspect-square w-full max-w-[120px] mx-auto md:mx-0 rounded-md overflow-hidden">
            <img
              src={productImage}
              alt={productName}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Inquiry Details */}
        <div className="flex-1 p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div>
              <h3 className="font-medium">{subject}</h3>
              <div className="text-sm text-muted-foreground">
                {isSeller ? `From: ${buyerName}` : `To: ${sellerName}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <InquiryStatusBadge status={status} />
              {hasUnreadMessages && (
                <Badge
                  variant="secondary"
                  className="bg-primary text-primary-foreground"
                >
                  New Message
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-2 text-sm">
            <div className="flex items-center text-muted-foreground">
              <CalendarIcon className="h-3.5 w-3.5 mr-1" />
              {formatDate(updatedAt)}
            </div>
          </div>

          {lastMessagePreview && (
            <div className="mt-2">
              <p className="text-sm line-clamp-2">
                <span className="font-medium">
                  {isBuyer ? sellerName : buyerName}:
                </span>{" "}
                {lastMessagePreview}
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to={`/inquiry-management/${id}`}>View Details</Link>
            </Button>
            {status === "new" && isSeller && (
              <Button size="sm" asChild>
                <Link to={`/inquiry-management/${id}`}>Respond Now</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Action Arrow (visible on larger screens) */}
        <div className="hidden lg:flex items-center p-4">
          <Button variant="ghost" size="icon" className="rounded-full" asChild>
            <Link to={`/inquiry-management/${id}`}>
              <ChevronRightIcon className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
